import React, { useState } from "react";
import jsonExport from "jsonexport/dist";

import DateFnsUtils from "@date-io/date-fns";
import { addDays, format } from "date-fns";

import { API, graphqlOperation } from "aws-amplify";
import { searchInventoryStorage } from "../../../graphql/report-queries";

import { Box, Card, Grid, Typography } from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";

import {
  Button,
  Datagrid,
  DateField,
  downloadCSV,
  FunctionField,
  ListContextProvider,
  NumberField,
  SimpleForm,
  TextField,
  Title,
  TopToolbar,
  useList,
  useListContext,
  useNotify,
} from "react-admin";

import { AccountDropDown } from "../../../components/common/AccountDropDown";
import { BackButton } from "../../../components/common/BackButton";
import { ExportIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { Pagination } from "../../../components/Pagination";

import { logError } from "../../../utils/appUtils";
import {
  InventoryStatus,
  MAX_INVENTORY_QUERY_LIMIT,
  Unit,
} from "../../../utils/constants";
import { getConversionValue, getUnitName } from "../../../utils/loadTypeUtils";
import { getTotalOnHandAsOfEndDate } from "../../resources/accounts/chargeRules/ChargeRuleUtils";
import { deepRedirect, getBackPath, trimDecimal } from "../../../utils/utils";

const getInventorySnapshotQuantityOnHand = (inventory, endDate) =>
  trimDecimal(getTotalOnHandAsOfEndDate(inventory, endDate));

const getSnapshotLabel = (snapshotDate) => {
  if (!snapshotDate) return "";
  return `As of ${new Date(snapshotDate).toLocaleDateString()}`;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildSnapshotInventory = (inventory, snapshotBoundaryDate) => {
  const snapshotQuantityOnHand = getInventorySnapshotQuantityOnHand(
    inventory,
    snapshotBoundaryDate,
  );
  const snapshotQuantityCommitted = 0;

  const snapshotMbf = getConversionValue(
    inventory,
    snapshotQuantityOnHand,
    Unit.MBF,
    inventory.inventoryUOM,
    "",
  );
  const snapshotMsf = getConversionValue(
    inventory,
    snapshotQuantityOnHand,
    Unit.MSF,
    inventory.inventoryUOM,
    "",
  );
  const snapshotLf = getConversionValue(
    inventory,
    snapshotQuantityOnHand,
    Unit.LF,
    inventory.inventoryUOM,
    "",
  );

  return {
    ...inventory,
    snapshotQuantityOnHand,
    snapshotQuantityCommitted,
    snapshotMbf,
    snapshotMsf,
    snapshotLf,
  };
};

const exporter = (inventories, snapshotDate) => {
  const label = getSnapshotLabel(snapshotDate)
    .replace(/\s+/g, "_")
    .replace(/\//g, "_");
  const reportForExport = inventories.map((inventory) => ({
    Received: inventory.receivedAt
      ? new Date(inventory.receivedAt).toLocaleDateString()
      : "",
    "Product Code": inventory.productCode,
    "Product Name": inventory.productName,
    "Order No.": `${inventory.poNumber || ""}`,
    "Car/Trl No.": `${inventory.railCarNumber || ""}`,
    "Qty. On Hand": inventory.snapshotQuantityOnHand,
    UOM: getUnitName(inventory.inventoryUOM),
    MBF: inventory.snapshotMbf,
    MSF: inventory.snapshotMsf,
    LF: inventory.snapshotLf,
  }));

  jsonExport(reportForExport, { rename: [] }, (err, csv) => {
    downloadCSV(csv, `inventory_snapshot_${label}`);
  });
};

const ListActions = (props) => {
  const {
    account,
    setAccount,
    snapshotDate,
    setSnapshotDate,
    getReportData,
    clearReportData,
    finalData,
  } = props;

  const isRunDisabled = () => {
    return !account?.id || !snapshotDate;
  };

  return (
    <Box mt={-4}>
      <TopToolbar>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={4}>
              <AccountDropDown
                source="accountId"
                label="Account"
                onAccountChange={(value) => {
                  setAccount(value);
                  clearReportData();
                }}
                allowEmpty={false}
                helperText={false}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Snapshot Date"
                variant="inline"
                inputVariant="outlined"
                margin="dense"
                fullWidth
                disableToolbar
                disableFuture
                autoOk
                required
                minDate={new Date(window.appUser?.reloadYard?.createdAt)}
                maxDate={new Date()}
                value={snapshotDate}
                onChange={(value) => {
                  setSnapshotDate(value);
                  clearReportData();
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                label="Run"
                variant="contained"
                color="default"
                onClick={getReportData}
                disabled={isRunDisabled()}
              />
            </Grid>
          </Grid>
        </MuiPickersUtilsProvider>
        <HelpButton />
        <Button
          label="Export"
          disabled={finalData?.length === 0}
          onClick={() => exporter(finalData, snapshotDate)}
        >
          <ExportIcon />
        </Button>
        <BackButton basePath="/reports" />
      </TopToolbar>
    </Box>
  );
};

export const InventorySnapshotReport = (props) => {
  const notify = useNotify();
  const reloadYardId = window.appUser?.reloadYardId
    ? window.appUser.reloadYardId
    : props.history?.location?.state?.reloadYardId;

  const [account, setAccount] = useState(null);
  const [snapshotDate, setSnapshotDate] = useState(new Date());
  const [finalData, setFinalData] = useState([]);
  const [unitTotals, setUnitTotals] = useState({});
  const [loading, setLoading] = useState(false);

  const clearReportData = () => {
    setFinalData([]);
    setUnitTotals({});
  };

  const getReportData = async () => {
    try {
      setLoading(true);
      const nextSnapshotDateString = format(
        addDays(snapshotDate, 1),
        "yyyy-MM-dd",
      );

      let inventories = [];
      let nextToken = null;

      do {
        const searchInput = {
          filter: {
            // id: { eq: "90fc1d14-a2a7-453d-a577-8a1e82f06f94" },
            // reloadYardCommodityId: {
            //   eq: "31aa7feb-67a1-4ec9-b77c-8f6ae60f6b63",
            // },
            // reloadYardCommodityId: {
            //   eq: "a9e52947-a348-4dc3-a44f-b1e611b8eaec",
            // },
            reloadYardId: { eq: reloadYardId },
            accountId: { eq: account.id },
            receivedAt: { lt: nextSnapshotDateString },
            or: [
              {
                status: { eq: InventoryStatus.STORED },
              },
              {
                and: [
                  { status: { eq: InventoryStatus.SHIPPED } },
                  {
                    or: [
                      { depletedAt: { gte: nextSnapshotDateString } },
                      { depletedAt: { exists: false } },
                      { depletedAt: { eq: "" } },
                      { depletedAt: { lte: 0 } },
                    ],
                  },
                ],
              },
            ],
          },
          sort: { field: "receivedAt", direction: "asc" },
          limit: MAX_INVENTORY_QUERY_LIMIT,
          nextToken,
        };

        const result = await API.graphql(
          graphqlOperation(searchInventoryStorage, searchInput),
        );
        const inventoryList = result.data.searchInventorys.items;
        inventories = inventories.concat(inventoryList);

        if (inventoryList.length < MAX_INVENTORY_QUERY_LIMIT) break;
        nextToken = result.data.searchInventorys.nextToken;
      } while (nextToken);

      const snapshotInventories = inventories
        .map((inventory) =>
          buildSnapshotInventory(inventory, nextSnapshotDateString),
        )
        .filter((inventory) => toNumber(inventory.snapshotQuantityOnHand) > 0);

      setFinalData(snapshotInventories);
      const totals = snapshotInventories.reduce(
        (accum, inventory) => {
          const mbf = Number(inventory.snapshotMbf);
          const msf = Number(inventory.snapshotMsf);
          const lf = Number(inventory.snapshotLf);
          const uom = inventory.inventoryUOM;
          const onHand = Number(inventory.snapshotQuantityOnHand);

          if (Number.isFinite(mbf)) accum.MBF += mbf;
          if (Number.isFinite(msf)) accum.MSF += msf;
          if (Number.isFinite(lf)) accum.LF += lf;
          if (uom && Number.isFinite(onHand)) {
            accum.uomTotals[uom] = (accum.uomTotals[uom] || 0) + onHand;
          }

          return accum;
        },
        { MBF: 0, MSF: 0, LF: 0, uomTotals: {} },
      );
      setUnitTotals(totals);
    } catch (err) {
      logError("InventorySnapshotReport:getReportData", err, notify);
      clearReportData();
    } finally {
      setLoading(false);
    }
  };

  const listContext = useList({
    ...props,
    data: finalData,
    ids: finalData.map(({ id }) => id),
    loading,
    loaded: !loading,
    filter: "",
    sort: { field: "receivedAt", order: "ASC" },
    perPage: 25,
  });

  return (
    <SimpleForm toolbar={false}>
      <ListContextProvider value={listContext}>
        <Title title={<ForceTitle title="Inventory Snapshot" />} />
        <ListActions
          {...props}
          account={account}
          setAccount={setAccount}
          snapshotDate={snapshotDate}
          setSnapshotDate={setSnapshotDate}
          getReportData={getReportData}
          clearReportData={clearReportData}
          finalData={finalData}
        />
        <Card>
          <InventorySnapshotGrid
            snapshotDate={snapshotDate}
            history={props.history}
            backPath={getBackPath(props)}
          />
          {finalData.length > 0 && (
            <PaginationToolbar unitTotals={unitTotals} />
          )}
        </Card>
      </ListContextProvider>
    </SimpleForm>
  );
};

const InventorySnapshotGrid = ({
  snapshotDate,
  history,
  backPath,
  ...props
}) => {
  const listContext = useListContext(props);
  const label = getSnapshotLabel(snapshotDate);

  if (!snapshotDate) return null;

  return (
    <Datagrid
      {...listContext}
      label={label}
      rowClick={(id, basePath, record) => {
        if (!record?.id) return false;
        deepRedirect(history, `/inventories/${record.id}`, backPath, {});
        return false;
      }}
    >
      <DateField source="receivedAt" label="Received" />
      <TextField source="productCode" label="Product Code" />
      <TextField source="productName" label="Product Name" />
      <TextField source="poNumber" label="PO Number" />
      <TextField source="railCarNumber" label="Car/Trl No." />
      <NumberField source="snapshotQuantityOnHand" label="Qty. On Hand" />
      <FunctionField
        source="inventoryUOM"
        label="UOM"
        render={(record) => getUnitName(record.inventoryUOM)}
      />
      <NumberField source="snapshotMbf" label="MBF" />
      <NumberField source="snapshotMsf" label="MSF" />
      <NumberField source="snapshotLf" label="LF" />
    </Datagrid>
  );
};

const PaginationToolbar = ({ unitTotals, ...props }) => {
  const listContext = useListContext(props);
  const toTotalValue = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 0;
    return trimDecimal(numericValue);
  };
  const uomTotals = unitTotals?.uomTotals || {};
  const uomEntries = Object.entries(uomTotals)
    .map(([uom, value]) => ({
      label: getUnitName(uom) || uom,
      value: toTotalValue(value),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const totals = [
    { label: "MBF", value: toTotalValue(unitTotals.MBF) },
    { label: "MSF", value: toTotalValue(unitTotals.MSF) },
    { label: "LF", value: toTotalValue(unitTotals.LF) },
  ];

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Grid item xs={4}>
        <Box pl={2}>
          <Typography style={{ fontWeight: "bold" }}>Totals</Typography>
          {uomEntries.map((entry) => (
            <Typography key={entry.label}>
              {entry.label}: {entry.value.toLocaleString()}
            </Typography>
          ))}
          {totals.map((entry) => (
            <Typography key={entry.label}>
              {entry.label}: {entry.value.toLocaleString()}
            </Typography>
          ))}
        </Box>
      </Grid>
      <Grid item xs={8}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};
